import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import {
    View,
    Text,
    TouchableOpacity,
    useWindowDimensions,
    StyleSheet,
    Animated,
} from 'react-native'
import RenderHtml from 'react-native-render-html'
import { useCssStyle } from '../../customHook/getCssStyle'
import { getStyleForRenderHTML } from '../../js/utility'
import { useTranslation } from 'react-i18next'

const MEASURE_PHASE = 'measure'
const READY_PHASE = 'ready'

const ShowMore = ({ htmlContent, numberOfLines = 2, textStyle = {} }) => {
    const getStyles = useCssStyle()
    const { width } = useWindowDimensions()
    const { t } = useTranslation()

    const [phase, setPhase] = useState(MEASURE_PHASE)
    const [isOverflowing, setIsOverflowing] = useState(false)
    const [expanded, setExpanded] = useState(false)

    const fullHeightRef = useRef(0)
    const animatedValue = useRef(new Animated.Value(0)).current

    const { baseStyle, systemFonts } = useMemo(
        () => getStyleForRenderHTML(textStyle),
        [textStyle]
    )

    const maxTruncatedHeight = useMemo(() => {
        const lineHeight =
            baseStyle?.lineHeight ??
            (baseStyle?.fontSize ? baseStyle.fontSize * 1.4 : 22)
        return lineHeight * numberOfLines
    }, [baseStyle, numberOfLines])

    useEffect(() => {
        setPhase(MEASURE_PHASE)
        setIsOverflowing(false)
        setExpanded(false)
        animatedValue.setValue(0)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [htmlContent])

    const handleMeasureLayout = useCallback(
        (e) => {
            const fullHeight = e.nativeEvent.layout.height
            fullHeightRef.current = fullHeight

            setIsOverflowing(fullHeight > maxTruncatedHeight + 2)
            setPhase(READY_PHASE)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [maxTruncatedHeight]
    )

    const handleToggle = useCallback(() => {
        const toValue = expanded ? 0 : 1

        Animated.timing(animatedValue, {
            toValue,
            duration: 300,
            useNativeDriver: false,
        }).start()

        setExpanded((prev) => !prev)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded])

    const animatedContainerStyle = {
        maxHeight: animatedValue.interpolate({
            inputRange: [0, 1],
            outputRange: [maxTruncatedHeight, fullHeightRef.current || maxTruncatedHeight],
        }),
        overflow: 'hidden',
    }

    const animatedOpacity = {
        opacity: animatedValue.interpolate({
            inputRange: [0, 1],
            outputRange: [0.8, 1],
        }),
    }

    const isMeasuring = phase === MEASURE_PHASE

    return (
        <View style={getStyles(['showMoreContainer'])}>
            {/* Hidden measurement */}
            {isMeasuring && (
                <View style={styles.invisible} onLayout={handleMeasureLayout}>
                    <RenderHtml
                        contentWidth={width}
                        source={{ html: htmlContent }}
                        baseStyle={baseStyle}
                        systemFonts={systemFonts}
                    />
                </View>
            )}

            {/* Animated content */}
            {!isMeasuring && (
                <Animated.View style={[animatedContainerStyle, animatedOpacity]}>
                    <RenderHtml
                        contentWidth={width}
                        source={{ html: htmlContent }}
                        baseStyle={baseStyle}
                        systemFonts={systemFonts}
                    />
                </Animated.View>
            )}

            {/* Button */}
            {!isMeasuring && isOverflowing && (
                <TouchableOpacity
                    onPress={handleToggle}
                    hitSlop={styles.hitSlop}
                    accessibilityRole="button"
                    accessibilityState={{ expanded }}
                >
                    <Text style={getStyles(['showMoreButtonText'])}>
                        {expanded
                            ? t('expandableText.showLess')
                            : t('expandableText.showMore')}
                    </Text>
                </TouchableOpacity>
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    invisible: {
        opacity: 0,
        position: 'absolute',
        zIndex: -1,
    },
    hitSlop: {
        top: 8,
        bottom: 8,
        left: 8,
        right: 8,
    },
})

export default ShowMore